/* oxlint-disable */
'use client';

import { useEffect, useState } from 'react';

import { isMobile } from 'react-device-detect';

import { userUIStore } from '@/stores/userUIStore';

import { getDataGamesTournaments } from '@/utils/helpers';

import { useToast } from '@/components/ui/use-toast';

import DesktopHeader from './shares/DesktopHeader';
import MobileHeader from './shares/MobileHeader';
import { TopPrize } from './shares/TopPrize';
import TournamentsGames from './shares/TournamentsGames';
import { TyCEvent } from './shares/TyCEvent';

interface TimeLeft {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

interface TournamentData {
  id: string;
  name: string;
  prize_pool: string;
  date_start: string;
  date_end: string;
  winner_prizes: Array<{ prize_sum: string; wager: string }>;
  leaderboard: {
    entries: Array<{
      position: string;
      nickname: string;
      rating: string;
    }>;
  };
  global_game_ids_to_game_info: Record<string, { id: string; title: string }>;
  rules: any;
}

export default function Tournaments({ slug, cmsTournament }: { slug: string, cmsTournament: any }) {
  const { isSidebarOpen } = userUIStore();
  const [tournament, setTournament] = useState<TournamentData | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
  });
  const [status, setStatus] = useState(''); // Tournament status
  const [poolPrize, setPoolPrize] = useState(0);
  const [topPrizes, setTopPrizes] = useState<any>([]);
  const [topPlayers, setTopPlayers] = useState<any>([]);
  const [otherPrizes, setOtherPrizes] = useState<any>([]);
  const [mergedArray, setMergedArray] = useState<any>([]);
  const [allGames, setAllGames] = useState<any>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (slug && cmsTournament && !cmsTournament?.isNetwork) {
      const fetchTournament = async () => {
        try {
          const response = await fetch(`/api/integrations/casino/tournaments?id=${slug}`, { cache: 'no-store' });
          const data = await response.json();
          setTournament(data);
          setPoolPrize(
            Math.floor(Number(data.prize_pool) * Number(cmsTournament?.currencyExchange)),
          );
          setTopPrizes(
            data.winner_prizes.slice(0, 3).map((prize: any) => {
              return {
                ...prize,
                prize_sum:
                    Math.floor(
                      (Number(prize.prize_sum)
                        * Number(cmsTournament?.currencyExchange)),
                    ),
              };
            }),
          );
          const otherPri = data.winner_prizes.slice(3).map((prize: any) => {
            const prizeSum = Math.floor(Number(prize.prize_sum) * Number(cmsTournament?.currencyExchange));

            return { ...prize, prize_sum: prizeSum };
          });
          setOtherPrizes(otherPri);

          setTopPlayers(data.leaderboard.entries.slice(0, 3));

          setMergedArray(
            data.leaderboard.entries.length !== 0 ? data.leaderboard.entries.slice(3).map((player: any, index: number) => {
              return {
                position: index + 4,
                nickname: player.nickname ?? '',
                rating: player.rating ?? '',
                prize: otherPri[index]?.prize_sum || 0,
              };
            }) : otherPri,
          );
        } catch (error) {
          console.error('Error fetching tournament:', error);
        }
      };

      fetchTournament();
    }
  }, [cmsTournament, slug]);

  useEffect(() => {
    const getGames = async () => {
      const response = await getDataGamesTournaments([], 'tournaments', cmsTournament?.provider);
      setAllGames(response.gameData);
    };

    getGames();
  }, [cmsTournament?.provider]);

  const games = tournament ? Object.values(tournament.global_game_ids_to_game_info).map((game) => {
    const provider = game.id.split(':')[0];
    return {
      identifier: game.id,
      provider,
      title: game.title,
      producer: provider,
      usd_only: cmsTournament?.currency === 'usd',
    };
  }) : [];

  useEffect(() => {
    const calculateTimeLeft = () => {
      const offset = 6 * 60 * 60 * 1000; // UTC-6 en milisegundos
      const startTime = new Date(cmsTournament?.startDate).getTime();
      const endTime = new Date(cmsTournament?.endDate).getTime();
      const now = new Date().getTime() - offset;
      const updateCountdown = (difference: number) => {
        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          );
          const minutes = Math.floor(
            (difference % (1000 * 60 * 60)) / (1000 * 60),
          );
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          setTimeLeft({
            days: days.toString().padStart(2, '0'),
            hours: hours.toString().padStart(2, '0'),
            minutes: minutes.toString().padStart(2, '0'),
            seconds: seconds.toString().padStart(2, '0'),
          });
        }
      };

      if (now < startTime) {
        // Tournament hasn't started yet, countdown to start
        setStatus('Próximamente');
        updateCountdown(startTime - now);
      } else if (now >= startTime && now <= endTime) {
        // Tournament is live, countdown to end
        setStatus('En curso');
        updateCountdown(endTime - now);
      } else {
        // Tournament has ended
        setStatus('Completado');
      }
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    return () => {
      return clearInterval(timer);
    };
  }, [cmsTournament]);

  useEffect(() => {
    if (status === 'Próximamente') {
      toast({
        title: 'El torneo aun no ha comenzado',
        description: `${timeLeft.days} días, ${timeLeft.hours} horas, ${timeLeft.minutes} minutos y ${timeLeft.seconds} segundos`,
      });
    }
  }, [status]);

  return (
    <>
      {isMobile && (
        <MobileHeader
          cmsTournament={cmsTournament}
          tournament={tournament}
          poolPrize={poolPrize}
          timeLeft={timeLeft}
          status={status}
        />
      )}
      <div
        className={`${isSidebarOpen ? 'pl-20 md:pl-64' : 'md:pl-20'} w-full pt-4 max-lg:pt-8 space-y-6 px-4`}
      >
        {!isMobile && (
          <DesktopHeader
            cmsTournament={cmsTournament}
            tournament={tournament}
            poolPrize={poolPrize}
            timeLeft={timeLeft}
            status={status}
          />
        )}
        {/* Eligible Games Carousel */}
        <TournamentsGames games={cmsTournament?.isNetwork ? allGames : games} />

        {/* Top Prizes Section */}
        {!cmsTournament?.isNetwork && (
          <TopPrize
            topPlayers={topPlayers}
            topPrizes={topPrizes}
            otherPrizes={otherPrizes}
            mergedArray={mergedArray}
          />
        )}
      </div>
      <TyCEvent tournament={cmsTournament?.isNetwork ? cmsTournament : tournament} isSidebarOpen={isSidebarOpen} showMore={showMore} setShowMore={setShowMore} />
    </>
  );
}
